import { Transition } from "@headlessui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    InputHTMLAttributes,
    useState,
    Fragment,
} from "react";

export default forwardRef(function TextInput(
    {
        type = "text",
        className = "",
        isFocused = false,
        icon = "",
        hasError = "",
        contextInfo = "",
        markAtTop = true,
        ...props
    }: InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> & {
        isFocused?: boolean;
        hasError?: string;
        icon?: string;
        contextInfo?: string;
        markAtTop?: boolean;
    },
    ref,
) {
    const localRef = useRef<HTMLInputElement>(null);
    const localTextareaRef = useRef<HTMLTextAreaElement>(null);
    const [isShownContextInfo, setIsShownContextInfo] =
        useState<boolean>(false);

    useImperativeHandle(ref, () => ({
        focus: () => localRef.current?.focus(),
    }));

    useEffect(() => {
        if (isFocused) {
            localRef.current?.focus();
        }
    }, []);

    if (type === "textarea") {
        return (
            <div
                className={`relative ${props["required"] ? "maxMd:max-w-[calc(100%_-_16px)]" : ""}`}
            >
                <div className="relative">
                    {localTextareaRef.current?.value && (
                        <label
                            className={`${props["disabled"] ? "opacity-30" : ""} text-arcticMist text-12 leading-16 absolute left-4 top-2`}
                        >
                            {props["placeholder"]}
                        </label>
                    )}
                    <textarea
                        {...props}
                        rows={8}
                        className={`pt-5 input-default ${localRef.current?.value ? "filled-input" : ""} ${className} ${hasError ? "border-crimsonAlert" : "border-royalPeriwinkle"}`}
                        ref={localTextareaRef}
                    />
                    {icon && (
                        <Icon
                            icon={icon}
                            className={`${hasError ? "text-crimsonAlert" : "text-royalPeriwinkle"} w-6 h-6 absolute right-4 top-3 ${props["disabled"] ? "opacity-30" : ""}`}
                        />
                    )}
                </div>
                {props["required"] && (
                    <div
                        className={`${markAtTop ? "absolute -right-5 -top-4" : "absolute -right-8 top-1/2 -translate-y-1/2"}`}
                    >
                        <Icon
                            icon={`lucide:asterisk`}
                            width={24}
                            height={24}
                            className={`${isShownContextInfo ? "text-accordorange" : "text-arcticMist"} cursor-pointer transition-theme`}
                            onMouseEnter={() => setIsShownContextInfo(true)}
                            onMouseLeave={() => setIsShownContextInfo(false)}
                            // onTouchStart={() => setIsShownContextInfo(true)}
                            // onTouchEnd={() => setIsShownContextInfo(false)}
                            onClick={() =>
                                setIsShownContextInfo((prev) => !prev)
                            }
                        />
                    </div>
                )}
                <Transition
                    as={Fragment}
                    show={isShownContextInfo && contextInfo != ""}
                    enter="transition-theme"
                    enterFrom="opacity-0"
                    leave="transition-theme"
                    leaveTo="opacity-0"
                >
                    <div className="rounded-[4px] z-20 border-1 border-royalPeriwinkle bg-deepIndigo p-2 absolute xl:-right-[84%] xl:top-2 xl:max-w-[315px] w-full text-arcticMist text-16 leading-6 maxMd:max-w-[295px] maxMd:-right-8 maxMd:-top-28 md:-right-8 md:-top-[150%]">
                        {contextInfo}
                    </div>
                </Transition>
            </div>
        );
    }

    if (type === "date") {
        return (
            <div
                className={`relative main-date-container ${props["required"] ? "maxMd:max-w-[calc(100%_-_16px)]" : ""}`}
            >
                <div className="relative sub-date-container">
                    <label
                        className={`${props["disabled"] ? "opacity-30" : ""} text-arcticMist text-12 leading-16 absolute left-4 top-2`}
                    >
                        {props["placeholder"]}
                    </label>
                    <input
                        {...props}
                        type={type}
                        className={`${type == "date" ? "input-date" : "input-default"} filled-input ${className} ${hasError ? "border-crimsonAlert" : "border-royalPeriwinkle"}`}
                        ref={localRef}
                    />
                    {icon && (
                        <Icon
                            icon={icon}
                            className={`${hasError ? "text-crimsonAlert" : "text-royalPeriwinkle"} w-6 h-6 absolute right-4 top-1/2 -translate-y-1/2`}
                        />
                    )}
                    <div className="date-overlay"></div>
                </div>
                {props["required"] && (
                    <div
                        className={`${markAtTop ? "absolute lg:-right-7 maxMd:-right-6 top-5" : "absolute -right-8 top-1/2 -translate-y-1/2"}`}
                    >
                        <Icon
                            icon={`lucide:asterisk`}
                            width={24}
                            height={24}
                            className={`${isShownContextInfo ? "text-accordorange" : "text-arcticMist"} cursor-pointer transition-theme`}
                            onMouseEnter={() => setIsShownContextInfo(true)}
                            onMouseLeave={() => setIsShownContextInfo(false)}
                        />
                    </div>
                )}
                <Transition
                    as={Fragment}
                    show={isShownContextInfo && contextInfo != ""}
                    enter="transition-theme"
                    enterFrom="opacity-0"
                    leave="transition-theme"
                    leaveTo="opacity-0"
                >
                    <div className="rounded-[4px] z-20 border-1 border-royalPeriwinkle bg-deepIndigo p-2 absolute -right-[84%] top-2 max-w-[315px] w-full text-arcticMist text-16 leading-6 maxMd:max-w-[295px] maxMd:max-w-[295px] maxMd:-right-8 maxMd:-top-28">
                        {contextInfo}
                    </div>
                </Transition>
            </div>
        );
    }

    return (
        <div
            className={`relative ${props["required"] ? "maxMd:max-w-[calc(100%_-_16px)]" : ""}`}
        >
            <div className="relative">
                {localRef.current?.value && (
                    <label
                        className={`${props["disabled"] ? "opacity-30" : ""} text-arcticMist text-12 leading-16 absolute left-4 top-2`}
                    >
                        {props["placeholder"]}
                    </label>
                )}
                <input
                    {...props}
                    type={type}
                    className={`${type == "date" ? "input-date" : "input-default"} ${localRef.current?.value ? "filled-input" : ""} ${className} ${hasError ? "border-crimsonAlert" : "border-royalPeriwinkle"}`}
                    ref={localRef}
                />
                {icon && (
                    <Icon
                        icon={icon}
                        className={`${hasError ? "text-crimsonAlert" : "text-royalPeriwinkle"} w-6 h-6 absolute right-4 top-1/2 -translate-y-1/2 ${props["disabled"] ? "opacity-30" : ""}`}
                    />
                )}
            </div>
            {props["required"] && (
                <div
                    className={`${markAtTop ? "absolute lg:-right-7 maxMd:-right-6 top-5" : "absolute -right-8 top-1/2 -translate-y-1/2"}`}
                >
                    <Icon
                        icon={`lucide:asterisk`}
                        width={24}
                        height={24}
                        className={`${isShownContextInfo ? "text-accordorange" : "text-arcticMist"} cursor-pointer transition-theme`}
                        onMouseEnter={() => setIsShownContextInfo(true)}
                        onMouseLeave={() => setIsShownContextInfo(false)}
                        // onTouchStart={() => setIsShownContextInfo(true)}
                        // onTouchEnd={() => setIsShownContextInfo(false)}
                        onClick={() => setIsShownContextInfo((prev) => !prev)}
                    />
                </div>
            )}
            <Transition
                as={Fragment}
                show={isShownContextInfo && contextInfo != ""}
                enter="transition-theme"
                enterFrom="opacity-0"
                leave="transition-theme"
                leaveTo="opacity-0"
            >
                <div className="rounded-[4px] z-20 border-1 border-royalPeriwinkle bg-deepIndigo p-2 absolute xl:-right-[84%] xl:top-2 xl:max-w-[315px] w-full text-arcticMist text-16 leading-6 maxMd:max-w-[295px] maxMd:-right-8 maxMd:-top-28 md:-right-8 md:-top-[150%]">
                    {contextInfo}
                </div>
            </Transition>
        </div>
    );
});
